import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, ViewEncapsulation, input, model } from '@angular/core';
import { StepperModule } from 'primeng/stepper';
import { RequireAtLeastOne } from '../helpers/utility-types';

@Component({
    selector: 'fcp-stepper',
    standalone: true,
    imports: [CommonModule, StepperModule],
    styleUrl: './fcp-stepper.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-stepper
            [value]="value()"
            [linear]="linear()"
            [transitionOptions]="transitionOptions()"
            (valueChange)="value.set($event)"
        >
            @if (orientation() === stepperOrientation.Horizontal) {
                <p-step-list>
                    @for (step of steps(); track step.value) {
                        <p-step
                            [value]="step.value"
                            [disabled]="step.disabled ?? false"
                            [class.fcp-stepper__step--disabled]="!!step.disabled"
                        >
                            <span class="fcp-stepper__title">
                                @if (step.titleTemplate) {
                                    <ng-container
                                        *ngTemplateOutlet="step.titleTemplate; context: { step: step }"
                                    ></ng-container>
                                } @else {
                                    {{ step.title }}
                                }
                            </span>
                        </p-step>
                    }
                </p-step-list>

                @if (!hidePanels()) {
                    <p-step-panels>
                        @for (step of steps(); track step.value) {
                            <p-step-panel [value]="step.value">
                                <ng-template #content let-activateCallback="activateCallback">
                                    @if (step.contentTemplate) {
                                        <ng-container
                                            *ngTemplateOutlet="
                                                step.contentTemplate;
                                                context: { step: step, activateCallback: activateCallback }
                                            "
                                        ></ng-container>
                                    } @else {
                                        {{ step.content }}
                                    }
                                </ng-template>
                            </p-step-panel>
                        }
                    </p-step-panels>
                }
            } @else {
                @for (step of steps(); track step.value) {
                    <p-step-item [value]="step.value">
                        <p-step
                            [disabled]="step.disabled ?? false"
                            [class.fcp-stepper__step--disabled]="!!step.disabled"
                        >
                            <span class="fcp-stepper__title">
                                @if (step.titleTemplate) {
                                    <ng-container
                                        *ngTemplateOutlet="step.titleTemplate; context: { step: step }"
                                    ></ng-container>
                                } @else {
                                    {{ step.title }}
                                }
                            </span>
                        </p-step>
                        <p-step-panel>
                            <ng-template #content let-activateCallback="activateCallback">
                                @if (!hidePanels()) {
                                    @if (step.contentTemplate) {
                                        <ng-container
                                            *ngTemplateOutlet="
                                                step.contentTemplate;
                                                context: { step: step, activateCallback: activateCallback }
                                            "
                                        ></ng-container>
                                    } @else {
                                        {{ step.content }}
                                    }
                                }
                            </ng-template>
                        </p-step-panel>
                    </p-step-item>
                }
            }
        </p-stepper>
    `
})
export class FcpStepperComponent {
    protected readonly stepperOrientation = FcpStepperOrientation;
    private readonly _transitionOptions = '400ms cubic-bezier(0.86, 0, 0.07, 1)';

    e2e = input<string | null>(null);
    steps = input<FcpStep[]>([]);
    value = model<number | undefined>(undefined);
    linear = input<boolean>(false);
    orientation = input<FcpStepperOrientation>(FcpStepperOrientation.Horizontal);
    hidePanels = input<boolean>(false);
    transitionOptions = input<string>(this._transitionOptions);
}

interface FcpStepBase {
    value: number;
    disabled?: boolean;
    title?: string;
    content?: string;
    titleTemplate?: TemplateRef<FcpStepTitleContext>;
    contentTemplate?: TemplateRef<FcpStepContentContext>;
}

type FcpStepWithTitle = RequireAtLeastOne<FcpStepBase, 'title' | 'titleTemplate'>;

export type FcpStep = FcpStepWithTitle;

export interface FcpStepTitleContext {
    step: FcpStep;
}

export interface FcpStepContentContext {
    step: FcpStep;
    activateCallback: (value: number) => void;
}

export enum FcpStepperOrientation {
    Horizontal = 'horizontal',
    Vertical = 'vertical'
}
